import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtTime } from "@/lib/format";
import { initialSupervision, raspberryView, supervisionView } from "@/lib/infraSupervision";
import { Stethoscope, Loader2, CheckCircle2, AlertTriangle, XCircle, MinusCircle, Play } from "lucide-react";

const SECRET_RE = /\b(TOKEN|API[_-]?KEY|PASSWORD|SECRET|Authorization|Bearer)\b(\s*[:=]\s*|\s+)([^\s"',]+)/gi;
const clientRedact = (t) => (typeof t === "string" ? t.replace(SECRET_RE, (m, a, b) => `${a}${b}***`) : t);

export function infraStatus(health) {
  if (health == null) return { color: "#525252", label: "Vérification…", detail: "Interrogation de l'Infra API", pulse: true };
  if (health.ok === true) return { color: "#22C55E", label: "Infra API : connectée", detail: "Le serveur d'infrastructure répond normalement." };
  const st = health.status;
  if (st === 504) return { color: "#F59E0B", label: "Infra API : dégradée", detail: health.error || "Délai dépassé (timeout)." };
  if (st === 401 || st === 403 || st === 400 || st === 404) return { color: "#F59E0B", label: "Infra API : dégradée", detail: health.error || "Réponse anormale de l'Infra API." };
  return { color: "#EF4444", label: "Infra API : hors ligne", detail: health.error || "Infra API injoignable." };
}

export function InfraStatusBanner({ supervision }) {
  const s = supervisionView(supervision || initialSupervision, fmtTime);
  return (
    <div data-testid="infra-status-banner" data-status={s.label} data-sup-state={(supervision || initialSupervision).status}
      className="flex items-center gap-3 rounded-sm border px-4 py-2.5 transition-colors duration-300"
      style={{ backgroundColor: `${s.color}14`, borderColor: `${s.color}55` }}>
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        {s.pulse && <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ backgroundColor: s.color }} />}
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
      </span>
      <span data-testid="infra-status-label" className="font-mono text-[11px] uppercase tracking-wider font-bold shrink-0" style={{ color: s.color }}>{s.label}</span>
      <span data-testid="infra-status-detail" className="font-mono text-[10px] text-[#A3A3A3] truncate min-w-0 flex-1">{s.detail}</span>
      <PermBadge />
    </div>
  );
}

export function RaspberrySupervision({ res, infraStatus: sup }) {
  let body;
  if (sup !== "ONLINE" && sup !== "DEGRADED") {
    body = <span className="text-[#525252]">Indisponible tant que l'Infra API n'est pas joignable.</span>;
  } else if (!res) {
    body = <span className="text-[#525252]">Chargement…</span>;
  } else if (!res.ok) {
    body = <span className="text-[#525252]">{res.status === 404 ? "Suivi par Raspberry non disponible (Infra API antérieure à INFRA.RESILIENCE.1)." : (res.error || "Suivi indisponible")}</span>;
  } else {
    const hosts = Object.entries(res.data?.hosts || {}).filter(([, h]) => h.ssh);
    body = hosts.length === 0 ? <span className="text-[#525252]">Aucun Raspberry sondé depuis le démarrage de l'Infra API.</span> : (
      <div className="flex flex-wrap gap-2">
        {hosts.map(([ip, h]) => {
          const v = raspberryView(h);
          return (
            <span key={ip} data-testid={`rpi-sup-${ip}`} data-state={v.state}
              className="inline-flex items-center gap-1.5 rounded-sm px-2 py-1 border" style={{ borderColor: `${v.color}55`, color: v.color }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: v.color }} />
              <span className="text-white">{ip}</span> {v.label}
              {h.ssh?.last_success_at && <span className="text-[#525252]">· OK {fmtTime(h.ssh.last_success_at)}</span>}
            </span>
          );
        })}
      </div>
    );
  }
  return (
    <div data-testid="rpi-supervision" className="bg-[#141414] border border-white/10 rounded-sm px-4 py-2.5 font-mono text-[10px] space-y-1.5">
      <div className="uppercase tracking-[0.2em] text-[#A3A3A3]">Raspberry — joignabilité (indépendante de l'Infra API)</div>
      {body}
    </div>
  );
}

// Cache module-level (>= 60s) pour ne pas spammer l'Infra API entre montages.
let _selftestCache = { ts: 0, status: undefined, loaded: false };
const SELFTEST_TTL = 60000;

const PERM_META = {
  ok: { color: "#22C55E", label: "Permissions OK", Icon: CheckCircle2 },
  degraded: { color: "#F59E0B", label: "Permissions à vérifier", Icon: AlertTriangle },
  error: { color: "#EF4444", label: "Erreur permissions", Icon: XCircle },
};

function PermBadge() {
  const [state, setState] = useState(() =>
    _selftestCache.loaded && Date.now() - _selftestCache.ts < SELFTEST_TTL
      ? { loading: false, status: _selftestCache.status }
      : { loading: true, status: undefined }
  );

  useEffect(() => {
    let cancelled = false;
    if (_selftestCache.loaded && Date.now() - _selftestCache.ts < SELFTEST_TTL) {
      setState({ loading: false, status: _selftestCache.status });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    api.get("/infrastructure/selftest")
      .then(({ data }) => {
        const status = data?.ok && data?.data ? data.data.status : null;
        _selftestCache = { ts: Date.now(), status, loaded: true };
        if (!cancelled) setState({ loading: false, status });
      })
      .catch(() => {
        _selftestCache = { ts: Date.now(), status: null, loaded: true };
        if (!cancelled) setState({ loading: false, status: null });
      });
    return () => { cancelled = true; };
  }, []);

  let meta, perm;
  if (state.loading) { meta = { color: "#525252", label: "Permissions…", Icon: Loader2, spin: true }; perm = "loading"; }
  else if (state.status && PERM_META[state.status]) { meta = PERM_META[state.status]; perm = state.status; }
  else { meta = { color: "#525252", label: "Permissions : non testé", Icon: MinusCircle }; perm = "none"; }

  return (
    <span data-testid="infra-perm-badge" data-perm={perm} title="Diagnostic des permissions serveur (voir Actions admin)"
      className="ml-2 shrink-0 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider rounded-sm px-2 py-1"
      style={{ color: meta.color, backgroundColor: `${meta.color}1A`, border: `1px solid ${meta.color}55` }}>
      <meta.Icon size={12} className={meta.spin ? "animate-spin" : ""} /> {meta.label}
    </span>
  );
}

export function SystemDetails({ system }) {
  const d = system?.ok ? system.data : null;
  return (
    <div className="bg-[#141414] border border-white/10 rounded-sm p-4" data-testid="system-details">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3] mb-2">Détails système</div>
      {!d ? <div className="font-mono text-[11px] text-[#525252]">{system?.error || "Données indisponibles"}</div> : (
        <pre className="font-mono text-[10px] text-[#A3A3A3] whitespace-pre-wrap break-words max-h-60 overflow-y-auto">{JSON.stringify(d, null, 2)}</pre>
      )}
    </div>
  );
}

export function EventsPanel({ events }) {
  return (
    <div className="bg-[#141414] border border-white/10 rounded-sm">
      <div className="px-3 py-2 border-b border-white/10 font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">Dernières actions infrastructure</div>
      <div className="max-h-64 overflow-y-auto p-2 space-y-1" data-testid="infra-events">
        {events.length === 0 && <div className="font-mono text-[11px] text-[#525252] p-2">Aucune action enregistrée</div>}
        {events.map((e) => (
          <div key={e.id} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-sm px-2 py-1 font-mono text-[10px]">
            <span className="text-[#525252]">{fmtTime(e.created_at)}</span>
            <span className="text-[#3B82F6] uppercase">{e.action}</span>
            <span className="text-white">{e.target}</span>
            <span className="ml-auto uppercase" style={{ color: e.status === "success" ? "#22C55E" : "#EF4444" }}>{e.status}</span>
            <span className="text-[#525252]">{e.user_email}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RawPanel({ title, res, testid, icon: Icon, note }) {
  if (res == null) return <div className="font-mono text-xs text-[#525252]">Chargement...</div>;
  const text = res.ok ? (typeof res.data === "string" ? res.data : JSON.stringify(res.data, null, 2)) : (res.error || "Indisponible");
  return (
    <div className="bg-[#141414] border border-white/10 rounded-sm" data-testid={testid}>
      <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2">
        {Icon && <Icon size={13} className="text-[#3B82F6]" />}
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#A3A3A3]">{title}</span>
        {!res.ok && <span className="ml-auto font-mono text-[9px] text-[#EF4444] uppercase">{res.error}</span>}
      </div>
      {note && <p className="px-3 pt-2 font-mono text-[9px] text-[#525252]">{note}</p>}
      <pre className="p-3 font-mono text-[10px] text-[#A3A3A3] whitespace-pre-wrap break-words max-h-[420px] overflow-y-auto">{clientRedact(text)}</pre>
    </div>
  );
}

function extractLogText(data) {
  if (data == null) return "";
  if (typeof data === "string") return data;
  if (typeof data.logs === "string") return data.logs;
  if (Array.isArray(data.logs)) return data.logs.join("\n");
  if (typeof data.raw === "string") return data.raw;
  if (Array.isArray(data)) return data.join("\n");
  return JSON.stringify(data, null, 2);
}

export function LogsBlock({ res, loading, search, boxRef }) {
  if (loading) return <div className="font-mono text-xs text-[#525252]">Chargement des logs...</div>;
  if (res == null) return <div className="font-mono text-[11px] text-[#525252]">Sélectionnez un service et cliquez sur Charger.</div>;
  if (!res.ok) return <div className="font-mono text-[11px] text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-sm px-3 py-2">{res.error || "Logs indisponibles"}</div>;
  let lines = clientRedact(extractLogText(res.data)).split("\n");
  if (search) lines = lines.filter((l) => l.toLowerCase().includes(search.toLowerCase()));
  return (
    <pre ref={boxRef} data-testid="logs-block" className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 font-mono text-[10px] text-[#A3A3A3] whitespace-pre-wrap break-words max-h-[480px] overflow-y-auto">
      {lines.join("\n") || "(aucune ligne)"}
    </pre>
  );
}

// ---------------- Self-test (diagnostic serveur, via proxy backend) ----------------
const STATUS_META = {
  ok: { color: "#22C55E", Icon: CheckCircle2, label: "OK" },
  warn: { color: "#F59E0B", Icon: AlertTriangle, label: "WARN" },
  degraded: { color: "#F59E0B", Icon: AlertTriangle, label: "DÉGRADÉ" },
  error: { color: "#EF4444", Icon: XCircle, label: "ERREUR" },
  skipped: { color: "#525252", Icon: MinusCircle, label: "IGNORÉ" },
};
const statusMeta = (s) => STATUS_META[s] || STATUS_META.skipped;

export function SelfTestPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [rpiIp, setRpiIp] = useState("");

  const run = async () => {
    setLoading(true); setResult(null); setError("");
    try {
      const params = rpiIp.trim() ? { rpi_ip: rpiIp.trim() } : {};
      const { data } = await api.get("/infrastructure/selftest", { params });
      if (data.ok && data.data) setResult(data.data);
      else { setError(data.error || "Infra API indisponible"); toast.error(data.error || "Diagnostic indisponible"); }
    } catch (e) {
      setError(apiErr(e, "Diagnostic refusé")); toast.error(apiErr(e, "Diagnostic refusé"));
    } finally { setLoading(false); }
  };

  const gm = result ? statusMeta(result.status) : null;
  return (
    <div className="bg-[#141414] border border-white/10 rounded-sm p-4 space-y-3" data-testid="selftest-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Stethoscope size={15} className="text-[#3B82F6]" />
        <span className="font-chivo font-black uppercase tracking-tight text-sm">Diagnostic serveur</span>
        <span className="font-mono text-[10px] text-[#A3A3A3]">Vérifie Docker, WireGuard, Nginx, fichiers, backups & SSH Raspberry.</span>
        <div className="ml-auto flex items-center gap-2">
          <input data-testid="selftest-rpi-ip" value={rpiIp} onChange={(e) => setRpiIp(e.target.value)} placeholder="IP Raspberry (optionnel)"
            className="bg-[#0A0A0A] border border-white/15 rounded-sm font-mono text-[10px] px-2 py-2 text-white outline-none w-48" />
          <button data-testid="selftest-run" onClick={run} disabled={loading}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider bg-[#3B82F6] rounded-sm px-3 py-2 hover:bg-[#3B82F6]/85 disabled:opacity-50">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} {loading ? "Diagnostic…" : "Lancer le diagnostic"}
          </button>
        </div>
      </div>

      {error && <div data-testid="selftest-error" className="font-mono text-[11px] text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-sm px-3 py-2">{error}</div>}

      {result && (
        <div className="space-y-2">
          <div className="flex items-center gap-2" data-testid="selftest-status" data-status={result.status}>
            <span className="font-mono text-[10px] uppercase tracking-wider text-[#A3A3A3]">Statut global :</span>
            <span className="flex items-center gap-1.5 font-mono text-[11px] uppercase font-bold rounded-sm px-2 py-1"
              style={{ color: gm.color, backgroundColor: `${gm.color}1A`, border: `1px solid ${gm.color}55` }}>
              <gm.Icon size={13} /> {result.status}
            </span>
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden" data-testid="selftest-checks">
            <table className="w-full text-left">
              <thead>
                <tr className="font-mono text-[9px] uppercase tracking-wider text-[#525252] border-b border-white/10">
                  <th className="px-3 py-2">Vérification</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2">Message</th>
                </tr>
              </thead>
              <tbody>
                {(result.checks || []).map((c) => {
                  const m = statusMeta(c.status);
                  return (
                    <tr key={c.name} data-testid={`selftest-check-${c.name}`} className="border-b border-white/5 font-mono text-[11px]">
                      <td className="px-3 py-2 text-white">{c.name}</td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1.5 uppercase text-[10px] font-bold" style={{ color: m.color }}>
                          <m.Icon size={12} /> {m.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-[#A3A3A3]">{c.message}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
